import { AppStorage } from 'storage/AppStorage';
import { ProfileService } from 'core/http/service/Profile.service';
import OnError from 'core/onError/OnError';
import OrigemCadastroDTO from 'models/origemCadastro/OrigemCadastroDTO';

// The origin is kept in a cookie (not local/sessionStorage) because Routes.tsx
// clears both storages on every unauthenticated load, and because it has to
// survive the round trip to the Keycloak register page.
const COOKIE_KEY = 'humane_origem_cadastro';
const COOKIE_EXPIRES_DAYS = 3;
const MAX_LENGTH = 255;

type OrigemCadastro = Omit<OrigemCadastroDTO, 'codigoPerfil'>;

const UTM_PARAMS: Record<string, keyof OrigemCadastro> = {
    utm_source: 'utmSource',
    utm_medium: 'utmMedium',
    utm_campaign: 'utmCampaign',
    utm_term: 'utmTerm',
    utm_content: 'utmContent',
};

const sanitize = (value: string | null): string => (value ?? '').trim().slice(0, MAX_LENGTH);

/**
 * Saves the UTM parameters of the URL in a cookie (last touch: a new visit
 * with UTMs overwrites it, a visit without UTMs keeps it).
 */
export const capturarOrigemCadastro = (search: string, pathname: string): void => {
    try {
        const params = new URLSearchParams(search);
        const possuiUtm = Object.keys(UTM_PARAMS).some(param => sanitize(params.get(param)).length > 0);

        if (!possuiUtm) return;

        const origem: OrigemCadastro = {
            utmSource: '',
            utmMedium: '',
            utmCampaign: '',
            utmTerm: '',
            utmContent: '',
            fbclid: sanitize(params.get('fbclid')),
            gclid: sanitize(params.get('gclid')),
            paginaEntrada: sanitize(pathname),
            referrer: sanitize(document.referrer),
            dataCaptura: new Date().toISOString(),
        };

        Object.entries(UTM_PARAMS).forEach(([param, field]) => {
            origem[field] = sanitize(params.get(param));
        });

        AppStorage.saveWithOptions(COOKIE_KEY, origem, {
            expires: COOKIE_EXPIRES_DAYS,
            path: '/',
            sameSite: 'lax',
            secure: window.location.protocol === 'https:',
        });
    } catch (error) {
        OnError.sendToAnalytics('capturarOrigemCadastro', String(error));
    }
};

const obterOrigemCadastro = async (): Promise<OrigemCadastro | null> => {
    try {
        const value = await AppStorage.get(COOKIE_KEY);
        return value ? JSON.parse(value) : null;
    } catch (error) {
        return null;
    }
};

/**
 * Sends the captured origin, if any, linked to the profile just created.
 * Never throws: a failure here must not break the registration flow. The
 * cookie is only removed after a successful request.
 */
export const registrarOrigemCadastro = async (codigoPerfil: number): Promise<void> => {
    const origem = await obterOrigemCadastro();

    if (!origem || !codigoPerfil) return;

    try {
        const response = await new ProfileService().registrarOrigemCadastro({ ...origem, codigoPerfil });

        if (response.status >= 200 && response.status < 300) {
            await AppStorage.remove(COOKIE_KEY);
        } else {
            OnError.sendToAnalytics('registrarOrigemCadastro', `status ${response.status}`);
        }
    } catch (error) {
        OnError.sendToAnalytics('registrarOrigemCadastro', String(error));
    }
};

/**
 * Discards the captured origin. Called when a user that already has a profile
 * logs in, so a later registration on the same (shared) browser does not
 * inherit a campaign from someone else.
 */
export const limparOrigemCadastro = async (): Promise<void> => {
    await AppStorage.remove(COOKIE_KEY);
};
